import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { logout, loginUser } from '../redux/authSlice';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, status, error } = useSelector((state) => state.auth);

  // Log state changes in development only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthContext state changed:', {
        hasUser: !!user,
        user,
        status,
        error
      });
    }
  }, [user, status, error]);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleLogin = useCallback(async (credentials) => {
    try {
      const result = await dispatch(loginUser(credentials)).unwrap();
      return result;
    } catch (err) {
      console.error('Login failed:', err);
      throw err;
    }
  }, [dispatch]);

  const value = useMemo(() => ({
    user,
    status,
    error,
    logout: handleLogout,
    login: handleLogin,
    isAuthenticated: !!user
  }), [user, status, error, handleLogout, handleLogin]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
